import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  inject
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ROLES } from 'src/app/features/authentication/authRoles.constants';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';

export interface ProductDataTab {
  labelKey: string;
  fallbackLabel: string;
  link: string;
}

@Component({
  selector: 'app-product-data-container',
  templateUrl: './product-data-container.component.html',
  styleUrls: ['./product-data-container.component.scss'],
  standalone: false
})
export class ProductDataContainerComponent implements OnInit, AfterViewInit, OnDestroy {
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  public translationService = inject(TranslationService);

  readonly ROLES = ROLES;

  @ViewChild('navContainer', { read: ElementRef }) navContainerRef: ElementRef;
  @ViewChild('tabNavBar', { read: ElementRef }) tabNavBarRef: ElementRef;
  @ViewChildren('shadowLink', { read: ElementRef }) shadowLinks: QueryList<ElementRef>;
  @ViewChild('shadowMoreLink', { read: ElementRef }) shadowMoreLinkRef: ElementRef;

  private resizeObserver: ResizeObserver;
  private rafHandle: number | null = null;
  private postFrameTimer: ReturnType<typeof setTimeout> | null = null;
  private routerSub: Subscription;
  private readonly onWindowResize = () => this.scheduleRecalculation();

  /**
   * matchMedia instance tracking the current device pixel ratio.
   * Listening for its 'change' event gives us reliable post-layout zoom
   * notifications in Firefox (where ResizeObserver/resize events may fire
   * before the new layout is settled on zoom-in).
   */
  private zoomMq: MediaQueryList | null = null;

  /** Tabs shown directly in the visible nav bar */
  displayedTabs: ProductDataTab[] = [];
  /** Tabs collapsed into the "More ▾" dropdown */
  overflowTabs: ProductDataTab[] = [];

  /** Permitteed tabs in their original alphabetical order (cached). */
  private _permittedTabs: ProductDataTab[] | null = null;

  /**
   * User-preferred ordering of permitted tabs.
   * Starts as a copy of `permittedTabs`; swaps update this array in-place.
   */
  private _tabOrder: ProductDataTab[] | null = null;

  /**
   * link → measured offsetWidth (px) for each tab.
   * Built once from the shadow nav; avoids order-mismatch issues when tabs are swapped.
   */
  private readonly tabWidthMap = new Map<string, number>();

  readonly allTabs: ProductDataTab[] = [
    { labelKey: 'adjustmentReasonText', fallbackLabel: 'Adjustment Reasons', link: '/home/productData/adjustmentReasons' },
    { labelKey: 'applicationText', fallbackLabel: 'Application', link: '/home/productData/application' },
    { labelKey: 'axleText', fallbackLabel: 'Axle', link: '/home/productData/axle' },
    { labelKey: 'casingCreditText', fallbackLabel: 'Casing Credit', link: '/home/productData/casingCredit' },
    { labelKey: 'CasingCreditRepairText', fallbackLabel: 'Casing Credit Repair', link: '/home/productData/casing-CreditRepair' },
    { labelKey: 'casingDefinitionText', fallbackLabel: 'Casing Definition', link: '/home/productData/casingDefinition' },
    { labelKey: 'casingFitmentText', fallbackLabel: 'Casing Fitment', link: '/home/productData/casingFitment' },
    { labelKey: 'casingPurchaseText', fallbackLabel: 'Casing Purchase', link: '/home/productData/casingPurchase' },
    { labelKey: 'casingSizeText', fallbackLabel: 'Casing Size', link: '/home/productData/casingSize' },
    { labelKey: 'customMoldText', fallbackLabel: 'Custom Mold', link: '/home/productData/customMold' },
    { labelKey: 'disposalChargeSetupText', fallbackLabel: 'Disposal Charges', link: '/home/productData/disposalCharges' },
    { labelKey: 'factoryCodeText', fallbackLabel: 'Factory Code', link: '/home/productData/factoryCode' },
    { labelKey: 'finishedProductText', fallbackLabel: 'Finished Product', link: '/home/productData/finishedProduct' },
    { labelKey: 'freeRepairText', fallbackLabel: 'Free Repair', link: '/home/productData/freeRepair' },
    { labelKey: 'makeText', fallbackLabel: 'Make', link: '/home/productData/make' },
    { labelKey: 'preferredTreadsText', fallbackLabel: 'Preferred Treads', link: '/home/productData/preferred-treads' },
    { labelKey: 'preMoldText', fallbackLabel: 'Pre Mold', link: '/home/productData/preMold' },
    { labelKey: 'rejectReasonsText', fallbackLabel: 'Reject Reasons', link: '/home/productData/rejectReasons' },
    { labelKey: 'repairsText', fallbackLabel: 'Repairs', link: '/home/productData/repairs' },
    { labelKey: 'rerunReworkReasonsText', fallbackLabel: 'Rerun/Rework Reasons', link: '/home/productData/rerunReworkReasons' }
  ];

  get canViewCasingGrade(): boolean {
    const roles = localStorage.getItem('roles') || '';
    return roles.includes(ROLES.view_casing_grade) || roles.includes(ROLES.edit_casing_grade);
  }

  get canViewWheelAndTireServices(): boolean {
    const roles = localStorage.getItem('roles') || '';
    return roles.includes(ROLES.view_services) || roles.includes(ROLES.edit_services);
  }

  /** All permitted tabs in stable alphabetical order (cached once). */
  get permittedTabs(): ProductDataTab[] {
    if (!this._permittedTabs) {
      const tabs = [...this.allTabs];
      if (this.canViewCasingGrade) {
        tabs.push(
          { labelKey: 'casingGradeText', fallbackLabel: 'Casing Grade', link: '/home/productData/casingGrade' },
          { labelKey: 'casingGradeTableText', fallbackLabel: 'Casing Grade Table', link: '/home/productData/casing-grade-table' }
        );
      }
      if (this.canViewWheelAndTireServices) {
        tabs.push({
          labelKey: 'wheelAndTireServicesText',
          fallbackLabel: 'Wheel and Tire Services',
          link: '/home/productData/wheelAndTireServices'
        });
      }
      this._permittedTabs = tabs.sort((a, b) => a.fallbackLabel.localeCompare(b.fallbackLabel));
    }
    return this._permittedTabs;
  }

  /**
   * The tabs in current display order.
   * Starts identical to `permittedTabs`; user swaps reorder this array.
   */
  get tabOrder(): ProductDataTab[] {
    if (!this._tabOrder) {
      this._tabOrder = [...this.permittedTabs];
    }
    return this._tabOrder;
  }

  /** Highlights the "More" button when the current page is in the overflow list. */
  get isMoreTabActive(): boolean {
    const url = this.router.url;
    return this.overflowTabs.some((tab) => url.startsWith(tab.link));
  }

  getLabel(tab: ProductDataTab): string {
    return this.translationService.translate(tab.labelKey) || tab.fallbackLabel;
  }

  /**
   * Called when the user selects a tab from the "More" dropdown.
   * Swaps the selected tab into the last visible position so it appears
   * immediately before the "More" button, and moves the displaced tab
   * into the dropdown — preserving the visible tab count.
   */
  onOverflowTabSelected(selectedTab: ProductDataTab): void {
    const order = [...this.tabOrder];
    const lastVisibleIdx = this.displayedTabs.length - 1;
    const selectedIdx = order.findIndex((t) => t.link === selectedTab.link);

    // Nothing to do if the tab is already visible or indexes are invalid
    if (lastVisibleIdx < 0 || selectedIdx < 0 || selectedIdx <= lastVisibleIdx) return;

    // Swap: selected overflow tab ↔ last visible tab
    [order[lastVisibleIdx], order[selectedIdx]] = [order[selectedIdx], order[lastVisibleIdx]];
    this._tabOrder = order;

    this.calculateVisibleTabs();
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    const url = this.router.url;
    if (url === '/home/productData' || url === '/home/productData/') {
      const first = this.permittedTabs[0];
      if (first) this.router.navigate([first.link]);
    }

    // Recalculate visible tabs on every navigation so the active tab stays visible
    this.routerSub = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.calculateVisibleTabs();
      this.cdr.detectChanges();
      this.scrollActiveTabIntoView();
    });
  }

  ngAfterViewInit(): void {
    // Build the width map once from the shadow nav (order-independent lookup)
    this.buildTabWidthMap();
    this.calculateVisibleTabs();

    this.ngZone.runOutsideAngular(() => {
      // ResizeObserver: handles sidebar toggles, panel resizes and Chrome/Edge zoom.
      // We observe the container, the document root (viewport-level zoom), and the
      // sidebar element itself so that opening/closing the sidebar immediately
      // triggers a recalculation without relying on a window-resize event.
      this.resizeObserver = new ResizeObserver(() => this.scheduleRecalculation());
      if (this.navContainerRef?.nativeElement) {
        this.resizeObserver.observe(this.navContainerRef.nativeElement);
      }
      this.resizeObserver.observe(document.documentElement);
      // NOTE: we do NOT observe mic-sidebar itself — observing a zero-width element
      // in Firefox can trigger a burst of repeated ResizeObserver callbacks, causing
      // instability when the sidebar is collapsed.  Sidebar toggle is detected
      // indirectly via the documentElement observer and window resize.

      // matchMedia zoom watcher: reliable post-layout zoom event for Firefox.
      this.setupZoomWatcher();

      // window resize: belt-and-suspenders
      window.addEventListener('resize', this.onWindowResize);
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.teardownZoomWatcher();
    window.removeEventListener('resize', this.onWindowResize);
    if (this.rafHandle !== null) cancelAnimationFrame(this.rafHandle);
    if (this.postFrameTimer !== null) clearTimeout(this.postFrameTimer);
    this.routerSub?.unsubscribe();
  }

  /**
   * Registers a matchMedia listener for the current device pixel ratio.
   * When the ratio changes (i.e. the user zooms), `onZoomChange` fires
   * AFTER Firefox has finished its reflow, guaranteeing fresh offsetWidth values.
   * Must be re-registered each time the ratio changes so the query stays current.
   */
  private setupZoomWatcher(): void {
    this.teardownZoomWatcher();
    this.zoomMq = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    this.zoomMq.addEventListener('change', this.onZoomChange);
  }

  private teardownZoomWatcher(): void {
    if (this.zoomMq) {
      this.zoomMq.removeEventListener('change', this.onZoomChange);
      this.zoomMq = null;
    }
  }

  /**
   * Called by the matchMedia watcher when the pixel ratio changes (zoom event).
   * Re-registers the watcher for the new ratio, then schedules a recalculation.
   * Because matchMedia fires post-layout, the RAF will read correct offsetWidths.
   */
  private readonly onZoomChange = (): void => {
    this.setupZoomWatcher(); // re-register at the new DPR before scheduling
    this.scheduleRecalculation();
  };

  /**
   * Schedules a recalculation using a two-phase pattern:
   *
   *   Phase 1 — requestAnimationFrame
   *     Coalesces all events that fire in the same frame into a single
   *     execution (cancels the previous RAF on each new event).
   *
   *   Phase 2 — setTimeout(0) inside the RAF
   *     Defers the actual offsetWidth reads to AFTER the current frame's
   *     layout/paint has fully committed.  This is critical for Firefox:
   *     when the user zooms in, Firefox fires resize/ResizeObserver/matchMedia
   *     before its reflow is finished, so a bare RAF still reads the old
   *     (pre-zoom) offsetWidth.  A setTimeout(0) queued from inside the RAF
   *     runs in the *next* task — after the frame pipeline has drained —
   *     at which point every browser has finalised the new layout.
   */
  private scheduleRecalculation(): void {
    if (this.rafHandle !== null) cancelAnimationFrame(this.rafHandle);
    if (this.postFrameTimer !== null) clearTimeout(this.postFrameTimer);

    this.rafHandle = requestAnimationFrame(() => {
      this.rafHandle = null;
      this.postFrameTimer = setTimeout(() => {
        this.postFrameTimer = null;
        this.ngZone.run(() => {
          this.calculateVisibleTabs();
          this.cdr.detectChanges();
          // Ensure active tab is scrolled into view after recalculation
          this.scrollActiveTabIntoView();
        });
      }, 0);
    });
  }

  /**
   * Scrolls the active tab into view if it exists and is partially off-screen.
   * Uses smooth scrolling for better UX.
   */
  private scrollActiveTabIntoView(): void {
    setTimeout(() => {
      const activeLink = this.navContainerRef?.nativeElement?.querySelector('.mat-mdc-tab-link.mdc-tab--active');
      if (activeLink) {
        activeLink.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }, 50); // Small delay to ensure DOM is updated
  }

  /**
   * Returns the pixel width currently occupied by the main navigation sidebar,
   * or 0 when the sidebar is collapsed/hidden.
   *
   * We deliberately avoid reading `aria-expanded` from the toggler button because
   * that button lives inside the Shadow DOM of `mic-sidebar-container` and is
   * invisible to `document.getElementById` in Firefox (strict shadow-DOM isolation).
   *
   * Instead we measure the sidebar element's own `offsetWidth` directly.
   * A fully-open sidebar is typically 200-300 px wide; a collapsed one may render
   * as 0 px (hidden) or ~56 px (icon strip).  We use a conservative threshold of
   * 80 px so that both a fully-hidden (0 px) and an icon-only (~56 px) sidebar are
   * treated as "not taking tab space", while only a clearly-open sidebar is
   * subtracted from the available tab width.
   */
  private getSidebarWidth(): number {
    const sidebar = document.querySelector('mic-sidebar[position="start"]') as HTMLElement | null;
    if (!sidebar) return 0;
    const width = sidebar.offsetWidth;
    // Only subtract when the sidebar is meaningfully open
    return width > 80 ? width : 0;
  }

  /**
   * Populates `tabWidthMap` from the shadow nav's rendered elements.
   * Keyed by `tab.link` so lookups remain correct after user-initiated swaps.
   * The shadow nav always renders `permittedTabs` in alphabetical order.
   */
  private buildTabWidthMap(): void {
    const tabs = this.permittedTabs;
    const els = this.shadowLinks?.toArray() ?? [];
    tabs.forEach((tab, i) => {
      const w = els[i]?.nativeElement?.offsetWidth;
      if (w) this.tabWidthMap.set(tab.link, w);
    });
  }

  /**
   * Splits `tabOrder` into `displayedTabs` (tabs that fit the nav bar) and
   * `overflowTabs` (tabs in the "More" dropdown).
   *
   * Uses `tabWidthMap` for per-tab widths so the result is correct regardless
   * of the current display ordering after user swaps.
   * Guarantees at least 1 tab is always visible even at extreme zoom.
   * Ensures the currently active tab is always visible in the nav bar.
   */
  private calculateVisibleTabs(): void {
    const allTabs = this.tabOrder;

    if (!this.navContainerRef?.nativeElement) {
      this.displayedTabs = allTabs;
      this.overflowTabs = [];
      return;
    }

    // Lazily populate the width map if it wasn't ready in ngAfterViewInit
    if (this.tabWidthMap.size === 0) {
      this.buildTabWidthMap();
    }

    // FIXED: Calculate width based on viewport instead of DOM elements
    // DOM elements can expand based on child content, causing inconsistent calculations
    // Use window.innerWidth and subtract sidebar width and padding (40px * 2 = 80px from .main-layout-card)
    const sidebarWidth: number = this.getSidebarWidth();
    const MAIN_LAYOUT_PADDING = 80; // 40px on each side from .main-layout-card
    const rawWidth: number = window.innerWidth - sidebarWidth - MAIN_LAYOUT_PADDING;

    // CRITICAL FIX: Actually set the max-width on the container to prevent expansion
    // This ensures the container never expands beyond the calculated width
    if (this.navContainerRef?.nativeElement) {
      this.navContainerRef.nativeElement.style.maxWidth = `${rawWidth}px`;
    }

    // Subtract the sidebar's width so tabs never render beneath it
    const containerWidth: number = Math.max(1, rawWidth);
    const moreWidth: number = this.shadowMoreLinkRef?.nativeElement?.offsetWidth ?? 104;

    // Find the currently active tab
    const currentUrl = this.router.url;
    const activeTabIndex = allTabs.findIndex((tab) => currentUrl.startsWith(tab.link));

    let totalWidth = 0;
    let splitIndex = allTabs.length;

    for (let i = 0; i < allTabs.length; i++) {
      const tabWidth = this.tabWidthMap.get(allTabs[i].link) ?? 120;
      const isLast = i === allTabs.length - 1;
      const required = totalWidth + tabWidth + (isLast ? 0 : moreWidth);

      if (required > containerWidth) {
        splitIndex = i;
        break;
      }
      totalWidth += tabWidth;
    }

    // Guarantee at least 1 tab is always visible
    if (allTabs.length > 0) splitIndex = Math.max(1, splitIndex);

    // If the active tab would be in overflow, ensure it stays visible
    if (activeTabIndex >= 0 && activeTabIndex >= splitIndex && splitIndex > 0) {
      // Temporarily adjust tabOrder to bring the active tab into view
      const reorderedTabs = [...allTabs];
      const activeTab = reorderedTabs[activeTabIndex];

      // Move the active tab to the last visible position
      reorderedTabs.splice(activeTabIndex, 1);
      reorderedTabs.splice(splitIndex - 1, 0, activeTab);

      this._tabOrder = reorderedTabs;

      // Update displayed and overflow tabs with the new order
      this.displayedTabs = reorderedTabs.slice(0, splitIndex);
      this.overflowTabs = reorderedTabs.slice(splitIndex);
    } else {
      // Normal case: active tab is already visible or there is no active tab
      this.displayedTabs = allTabs.slice(0, splitIndex);
      this.overflowTabs = allTabs.slice(splitIndex);
    }
  }
}
